import React, { useCallback, useEffect, useState } from "react";
import api, { API_URL, formatApiError, getToken } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_ADMIN } from "@/lib/nav";
import { Database, Download, Upload, Trash2, Pencil, RefreshCw, X, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function AdminDatabase() {
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState({ items: [], total: 0, skip: 0, limit: 50 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // {id, text}
  const [saving, setSaving] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);

  const loadCollections = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/db/collections");
      setCollections(data.collections);
      if (!selected && data.collections.length) setSelected(data.collections[0].name);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [selected]);

  const loadDocs = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/db/collections/${selected}`, { params: { limit: docs.limit, skip: docs.skip, q: q || undefined } });
      setDocs({ items: data.items, total: data.total, skip: data.skip, limit: data.limit });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, docs.skip, docs.limit, q]);

  useEffect(() => { loadCollections(); }, [loadCollections]);
  useEffect(() => { setDocs((d) => ({ ...d, skip: 0 })); }, [selected]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const del = async (id) => {
    if (!window.confirm(`Hapus dokumen '${id}' dari koleksi '${selected}'?\nTindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/admin/db/collections/${selected}/${id}`);
      toast.success("Dokumen dihapus");
      loadDocs();
      loadCollections();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (doc) => {
    setEditing({ id: doc.id, text: JSON.stringify(doc, null, 2) });
  };

  const saveEdit = async () => {
    let payload;
    try { payload = JSON.parse(editing.text); }
    catch { return toast.error("JSON tidak valid"); }
    setSaving(true);
    try {
      await api.put(`/admin/db/collections/${selected}/${editing.id}`, payload);
      toast.success("Dokumen tersimpan");
      setEditing(null);
      loadDocs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const downloadHref = (path) => {
    const t = getToken();
    return `${API_URL}${path}${t ? `?auth=${encodeURIComponent(t)}` : ""}`;
  };

  return (
    <Layout nav={NAV_ADMIN}>
      <PageTitle title="Database Admin" action={
        <div className="flex flex-wrap gap-2">
          <a href={downloadHref("/admin/db/backup")} target="_blank" rel="noreferrer" className="pp-btn-primary" data-testid="db-backup-full">
            <Download className="w-4 h-4" /> Backup FULL (ZIP)
          </a>
          {selected && (
            <a href={downloadHref(`/admin/db/backup/${selected}`)} target="_blank" rel="noreferrer" className="pp-btn-secondary" data-testid="db-backup-one">
              <Download className="w-4 h-4" /> Backup {selected}.json
            </a>
          )}
          <button onClick={() => setRestoreOpen(true)} className="pp-btn-secondary" data-testid="db-restore-open">
            <Upload className="w-4 h-4" /> Restore
          </button>
          <button onClick={() => { loadCollections(); loadDocs(); }} className="pp-btn-ghost" data-testid="db-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      } />

      {/* Integrasi Google Drive dihapus — backup bulanan hanya via email */}

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        <aside className="pp-card p-3 max-h-[70vh] overflow-y-auto" data-testid="db-sidebar">
          <div className="flex items-center gap-2 px-2 py-1 mb-2 text-xs font-semibold text-slate-500 uppercase">
            <Database className="w-3.5 h-3.5" /> Koleksi ({collections.length})
          </div>
          <ul className="space-y-0.5">
            {collections.map((c) => (
              <li key={c.name}>
                <button
                  onClick={() => setSelected(c.name)}
                  className={`w-full flex justify-between items-center px-3 py-2 rounded-md text-sm text-left transition ${selected === c.name ? "bg-indigo-600 text-white font-semibold" : "hover:bg-slate-100 text-slate-700"}`}
                  data-testid={`db-coll-${c.name}`}
                >
                  <span className="truncate">{c.name}</span>
                  <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${selected === c.name ? "bg-indigo-500 text-white" : "bg-slate-100 text-slate-500"}`}>{c.count}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <main className="pp-card p-4 min-h-[70vh]" data-testid="db-main">
          {!selected ? (
            <div className="text-center text-slate-400 italic py-20">Pilih koleksi di kiri.</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <h3 className="font-semibold text-slate-900 text-lg">{selected}</h3>
                <span className="text-xs text-slate-500">Total {docs.total} dokumen · Menampilkan {docs.items.length}</span>
                <input
                  className="pp-input ml-auto max-w-xs"
                  placeholder="Cari nama/nis/username/id/judul…"
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setDocs((d) => ({ ...d, skip: 0 })); }}
                  data-testid="db-search"
                />
              </div>

              {loading ? (
                <div className="text-center py-20 text-slate-400 italic">Memuat…</div>
              ) : docs.items.length === 0 ? (
                <div className="text-center py-20 text-slate-400 italic">Belum ada dokumen.</div>
              ) : (
                <div className="overflow-auto max-h-[60vh] border border-slate-200 rounded-md">
                  <table className="w-full text-xs" data-testid="db-table">
                    <thead className="sticky top-0 bg-indigo-600 text-white">
                      <tr>
                        <th className="text-left px-3 py-2 w-8">#</th>
                        <th className="text-left px-3 py-2 w-56">id</th>
                        <th className="text-left px-3 py-2">preview</th>
                        <th className="text-right px-3 py-2 w-32">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {docs.items.map((d, i) => (
                        <tr key={d.id || i} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2 text-slate-400 font-mono">{docs.skip + i + 1}</td>
                          <td className="px-3 py-2 font-mono text-[10px] break-all">{d.id || "—"}</td>
                          <td className="px-3 py-2 font-mono text-[10px] text-slate-600 truncate max-w-md">
                            {JSON.stringify(d).slice(0, 220)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="inline-flex gap-1">
                              <button onClick={() => openEdit(d)} className="pp-btn-ghost text-xs" data-testid={`db-edit-${d.id}`}>
                                <Pencil className="w-3 h-3" /> Edit
                              </button>
                              <button onClick={() => del(d.id)} className="pp-btn-danger" data-testid={`db-del-${d.id}`}>
                                <Trash2 className="w-3 h-3" /> Hapus
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {docs.total > docs.limit && (
                <div className="flex items-center justify-between mt-3 text-xs text-slate-600">
                  <button disabled={docs.skip <= 0} onClick={() => setDocs((d) => ({ ...d, skip: Math.max(0, d.skip - d.limit) }))} className="pp-btn-secondary disabled:opacity-40" data-testid="db-prev">
                    ← Sebelumnya
                  </button>
                  <span>Hlm {Math.floor(docs.skip / docs.limit) + 1} dari {Math.ceil(docs.total / docs.limit)}</span>
                  <button disabled={docs.skip + docs.limit >= docs.total} onClick={() => setDocs((d) => ({ ...d, skip: d.skip + d.limit }))} className="pp-btn-secondary disabled:opacity-40" data-testid="db-next">
                    Berikutnya →
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {editing && (
        <EditModal
          collection={selected}
          editing={editing}
          setEditing={setEditing}
          onSave={saveEdit}
          saving={saving}
        />
      )}
      {restoreOpen && (
        <RestoreModal
          selectedCollection={selected}
          onClose={() => setRestoreOpen(false)}
          onDone={() => { loadCollections(); loadDocs(); }}
        />
      )}
    </Layout>
  );
}

function EditModal({ collection, editing, setEditing, onSave, saving }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="db-edit-modal">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h4 className="font-semibold">Edit {collection} · <span className="font-mono text-xs text-slate-500">{editing.id}</span></h4>
          <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 flex-1 overflow-auto">
          <textarea
            rows={20}
            className="w-full font-mono text-xs border border-slate-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={editing.text}
            onChange={(e) => setEditing({ ...editing, text: e.target.value })}
            data-testid="db-edit-textarea"
          />
          <p className="text-xs text-slate-500 mt-2">Ubah nilai apapun. Pastikan JSON tetap valid & field <code>id</code> tidak berubah.</p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={() => setEditing(null)} className="pp-btn-secondary">Batal</button>
          <button onClick={onSave} disabled={saving} className="pp-btn-primary" data-testid="db-edit-save">
            <Save className="w-4 h-4" /> {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RestoreModal({ selectedCollection, onClose, onDone }) {
  const [scope, setScope] = useState("collection"); // collection | full
  const [mode, setMode] = useState("append");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return toast.error("Pilih file dulu");
    if (mode === "replace" && !window.confirm("MODE REPLACE akan menghapus data yang ada sebelum import. Yakin lanjutkan?")) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const url = scope === "full"
        ? `/admin/db/restore?mode=${mode}`
        : `/admin/db/restore/${selectedCollection}?mode=${mode}`;
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Restore selesai");
      console.log("Restore result:", data);
      onDone();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="db-restore-modal">
      <div className="bg-white rounded-lg w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h4 className="font-semibold flex items-center gap-2"><Upload className="w-4 h-4" /> Restore Backup</h4>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Restore akan menulis data ke database. Gunakan mode <b>Append</b> untuk aman (skip duplikat), atau <b>Replace</b> untuk mengganti seluruh isi koleksi.</span>
          </div>
          <div>
            <label className="pp-label">Cakupan</label>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-1"><input type="radio" checked={scope === "collection"} onChange={() => setScope("collection")} /> Satu Koleksi (JSON)</label>
              <label className="flex items-center gap-1"><input type="radio" checked={scope === "full"} onChange={() => setScope("full")} /> Full Database (ZIP)</label>
            </div>
          </div>
          {scope === "collection" && (
            <div>
              <label className="pp-label">Koleksi Tujuan</label>
              <input readOnly className="pp-input bg-slate-50" value={selectedCollection || "—"} />
            </div>
          )}
          <div>
            <label className="pp-label">Mode</label>
            <select className="pp-input" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="db-restore-mode">
              <option value="append">Append — insert & skip duplikat id</option>
              <option value="replace">Replace — hapus dulu lalu insert semua</option>
            </select>
          </div>
          <div>
            <label className="pp-label">File</label>
            <input type="file" accept={scope === "full" ? ".zip" : ".json"} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="db-restore-file" />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} className="pp-btn-secondary">Batal</button>
          <button onClick={submit} disabled={busy || !file} className="pp-btn-primary" data-testid="db-restore-submit">
            {busy ? "Memproses…" : "Restore"}
          </button>
        </div>
      </div>
    </div>
  );
}
